import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import 'overlayscrollbars/overlayscrollbars.css';
import { cn } from '@/shared/lib/utils/classnames';
import { parseTimestampToSeconds } from '@/shared/lib/utils/timestamp';
import {
  useAutoScroll,
  useCurrentTime,
  useYoutubePlayerStore,
} from '@/shared/store/youtube-player';
import type { TranscriptBlock as TranscriptBlockType } from '@/shared/types/blocks';

type TranscriptBlockProps = Omit<TranscriptBlockType, 'type' | 'id'> & {
  /** 연결된 YouTube 영상 ID (RowBlock에서 전달) */
  linkedVideoId?: string;
};

/**
 * 현재 재생 시간에 해당하는 활성 세그먼트 인덱스 찾기
 *
 * @param items 타임스탬프 배열
 * @param currentTime 현재 재생 시간 (초)
 * @returns 활성 세그먼트 인덱스
 */
function findActiveSegmentIndex(items: Array<{ timestamp: string }>, currentTime: number): number {
  if (currentTime <= 0) return 0;

  let activeIndex = 0;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!item) continue;
    const segmentTime = parseTimestampToSeconds(item.timestamp);
    if (segmentTime <= currentTime) {
      activeIndex = i;
    } else {
      break;
    }
  }
  return activeIndex;
}

/**
 * TranscriptBlock - 자막/스크립트
 *
 * YouTube 영상과 연동하여 자동 스크롤 및 클릭 점프를 지원합니다.
 *
 * Figma 디자인 기준:
 * - 카드 컨테이너 (테두리, 패딩)
 * - "전체 스크립트" 타이틀
 * - 타임스탬프: 파란색 tint 뱃지 (bg-[#e6f1ff], text-primary)
 * - 스크롤 가능한 콘텐츠 영역
 *
 * 높이: 비디오 영역과 동기화 (부모 높이에 맞춤)
 */
export function TranscriptBlock({ items, linkedVideoId }: TranscriptBlockProps) {
  const scrollContainerRef = useRef<OverlayScrollbarsComponentRef<'div'>>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const lastScrolledIndexRef = useRef<number>(-1);

  // Store subscriptions
  const currentTime = useCurrentTime(linkedVideoId);
  const autoScrollEnabled = useAutoScroll(linkedVideoId);
  const { requestSeek, toggleAutoScroll } = useYoutubePlayerStore();

  // 활성 세그먼트 계산
  const activeIndex = useMemo(
    () => findActiveSegmentIndex(items, currentTime),
    [items, currentTime],
  );

  // ============================================================================
  // AUTO SCROLL (컨테이너 내부만 스크롤, 페이지 스크롤 영향 없음)
  // ============================================================================

  useEffect(() => {
    if (!autoScrollEnabled || !linkedVideoId) return;
    if (activeIndex === lastScrolledIndexRef.current) return;

    const activeElement = itemRefs.current.get(activeIndex);
    const osInstance = scrollContainerRef.current?.osInstance();
    const viewport = osInstance?.elements().viewport;

    if (activeElement && viewport) {
      // 컨테이너 내부에서 요소의 상대 위치 계산
      const elementTop = activeElement.offsetTop;
      const elementHeight = activeElement.offsetHeight;
      const viewportHeight = viewport.clientHeight;

      // 요소를 중앙에 위치시키는 스크롤 위치 계산
      const targetScrollTop = elementTop - viewportHeight / 2 + elementHeight / 2;

      // 컨테이너 내부만 스크롤 (페이지 스크롤에 영향 없음)
      viewport.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth',
      });

      lastScrolledIndexRef.current = activeIndex;
    }
  }, [activeIndex, autoScrollEnabled, linkedVideoId]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleTimestampClick = useCallback(
    (timestamp: string) => {
      if (!linkedVideoId) return;
      const seconds = parseTimestampToSeconds(timestamp);
      requestSeek(linkedVideoId, seconds);
    },
    [linkedVideoId, requestSeek],
  );

  const handleAutoScrollToggle = useCallback(() => {
    if (linkedVideoId) {
      toggleAutoScroll(linkedVideoId);
    }
  }, [linkedVideoId, toggleAutoScroll]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="flex flex-col gap-3 bg-white border border-[#d9dde0] rounded-[6px] px-4 py-5 overflow-hidden h-full">
      {/* 헤더: 타이틀 + 자동 스크롤 토글 */}
      <div className="flex items-center justify-between shrink-0">
        <h3 className="font-bold text-[16px] text-black leading-[1.35]">전체 스크립트</h3>

        {linkedVideoId && (
          <button
            type="button"
            onClick={handleAutoScrollToggle}
            className={cn(
              'px-2.5 py-1.5 rounded-lg text-[12px] font-semibold transition-all cursor-pointer',
              autoScrollEnabled
                ? 'bg-primary text-white shadow-sm'
                : 'bg-[#f5f5f5] text-[#7c7e80] hover:bg-[#ebebeb]',
            )}
          >
            {autoScrollEnabled ? '자동 스크롤 켜짐' : '자동 스크롤 꺼짐'}
          </button>
        )}
      </div>

      {/* 스크립트 목록 - OverlayScrollbars 적용, 부모 높이에 맞춤 */}
      <OverlayScrollbarsComponent
        ref={scrollContainerRef}
        className="flex-1 min-h-0"
        options={{
          overflow: {
            x: 'hidden', // 가로 스크롤 비활성화
          },
          scrollbars: {
            visibility: 'auto',
            autoHide: 'scroll',
            autoHideDelay: 400,
          },
        }}
      >
        <div className="flex flex-col gap-3 w-full">
          {items.map((item, index) => {
            const isActive = linkedVideoId && index === activeIndex;

            return (
              <button
                type="button"
                key={`${item.timestamp}-${index}`}
                ref={(el) => {
                  if (el) itemRefs.current.set(index, el);
                  else itemRefs.current.delete(index);
                }}
                onClick={() => handleTimestampClick(item.timestamp)}
                className={cn(
                  'flex gap-2 items-start transition-colors duration-200 rounded-md text-left cursor-pointer',
                  isActive ? 'bg-primary/5 -mx-2 px-2 py-1' : 'hover:bg-muted/50 -mx-2 px-2 py-1',
                )}
              >
                {/* 타임스탬프 뱃지 */}
                <span
                  className={cn(
                    'inline-flex items-center justify-center px-1 py-px rounded-[2px]',
                    'text-[12px] font-bold leading-[1.35] w-[48px] shrink-0',
                    'transition-colors',
                    isActive ? 'bg-primary text-white' : 'bg-[#e6f1ff] text-primary',
                  )}
                >
                  {item.timestamp}
                </span>

                {/* 텍스트 */}
                <span
                  className={cn(
                    'flex-1 text-[12px] leading-[1.5] font-normal transition-colors duration-200',
                    isActive ? 'text-black' : 'text-[#3b3c3d]',
                  )}
                >
                  {item.text}
                </span>
              </button>
            );
          })}
        </div>
      </OverlayScrollbarsComponent>
    </div>
  );
}
